'use client';
import React, { Suspense } from 'react';
import styles from './NewArticleLayoutHeader.module.scss';

import {
  ArticleData,
  authorTypeGenerator,
  calculateReadTime,
  Campaigns,
  isPaywallArticle,
  isProOnlyPost,
} from '@benzinga/article-manager';

import Hooks from '@benzinga/hooks';
import { checkDeviceType, DeviceType } from '@benzinga/device-utils';
import { ErrorBoundary, EdgeBadge, ProBadge } from '@benzinga/core-ui';
import type { Mode } from '@benzinga/ads-utils';
import { truncate } from '@benzinga/utils';
import { IsUserPaywalledReturn } from '@benzinga/user-context';
import { BzImage } from '@benzinga/image';
import { NodeLayout } from '@benzinga/content-manager';

import { DateContent } from './DateContent';
import { ArticleFeaturedTicker } from '../entities';
import { NewAuthorContent } from './NewAuthorContent';
import { ArticleLayoutHeader as ArticleLayoutEditorBox } from './ArticleLayoutHeader';
import { AdvertiserDisclosure } from './AdvertiserDisclosure';
import { ArticleFeaturedTickersList } from './ArticleFeaturedTickersList';

export const ARTICLE_HEADER_BACKGROUND_IMAGE =
  'https://cdn.benzinga.com/files/images/story/2025/07/24/article-header-background-image.png';

const FlipboardButton = React.lazy(() => import('./FlipboardButton').then(m => ({ default: m.FlipboardButton })));
const GoogleNewsButton = React.lazy(() => import('./GoogleNewsButton').then(m => ({ default: m.GoogleNewsButton })));
const AppleNewsButton = React.lazy(() => import('./AppleNewsButton').then(m => ({ default: m.AppleNewsButton })));

const CallToActionForm = React.lazy(() =>
  import('@benzinga/forms-ui').then(module => {
    return { default: module.CallToActionForm };
  }),
);

export interface TaboolaSettings {
  placementMethod?: 'interval' | 'localStorage' | 'below-article';
  unitMode?: Mode;
  unitKey?: string;
}

interface ArticleLayoutHeaderProps {
  articleData: ArticleData;
  campaigns?: Campaigns | null;
  deviceType: DeviceType | null;
  featuredTickers: ArticleFeaturedTicker[];
  googleNewsUrlKey: 'benzinga' | 'benzingaIndia';
  headerRef?: (node: HTMLDivElement | null) => void;
  hideTopPanel?: boolean;
  isBot: boolean;
  isDraft: boolean;
  isEditor: boolean;
  isTemplate?: boolean;
  paywall: IsUserPaywalledReturn;
  showAdvertiserDisclosure: boolean;
  showCommentButton?: boolean;
  showFullProHeadline?: boolean;
  toggleCommentsDrawer?: () => void;
  baseUrl?: string;
  layout?: NodeLayout | null;
}

export const NewArticleLayoutHeader: React.FC<ArticleLayoutHeaderProps> = React.memo(
  ({
    articleData,
    baseUrl,
    deviceType,
    featuredTickers,
    googleNewsUrlKey,
    headerRef,
    hideTopPanel = false,
    isDraft = false,
    isEditor,
    isTemplate = false,
    layout,
    showAdvertiserDisclosure = false,
    showFullProHeadline = false,
  }) => {
    const isApple = Hooks.useHydrate(checkDeviceType().isApple(), false);
    const authorName =
      articleData?.author?.firstname && articleData?.author?.lastname
        ? `${articleData?.author.firstname} ${articleData?.author.lastname}`
        : articleData?.author?.name || articleData?.name;

    const createdAt = articleData.createdAt ? new Date(articleData.createdAt).toISOString() : null;
    const contentType = articleData?.type;
    const isProOnly = isProOnlyPost(articleData);

    const title = showFullProHeadline
      ? (articleData.title ?? '')
      : articleData?.isHeadline
        ? truncate(articleData.title ?? '', 45)
        : (articleData.title ?? '');

    const hasBadges = isProOnly || isPaywallArticle(articleData);
    const sidebarLgMinH = featuredTickers[0]?.type === 'CRYPTO' ? 'lg:min-h-[500px]' : 'lg:min-h-[600px]';

    return (
      <div
        className={`${styles.container}${isTemplate ? ` ${styles.isTemplate}` : ''} article-layout-main-header flex flex-col`}
        ref={headerRef}
      >
        {!isTemplate && (
          <ArticleLayoutEditorBox
            articleData={articleData}
            baseUrl={baseUrl}
            isEditor={isEditor}
            isMobile={deviceType === 'mobile'}
            layout={layout}
          />
        )}
        {!isTemplate && (
          <BzImage
            alt=""
            fetchPriority="high"
            layout="fill"
            loader={({ src, width }) => `${src}?quality=85&auto=webp&width=${width}`}
            loaderProps={{
              width: deviceType === 'mobile' ? 450 : 1300,
            }}
            objectFit="cover"
            preload={true}
            src={ARTICLE_HEADER_BACKGROUND_IMAGE}
          />
        )}
        <div className="flex relative overflow-hidden">
          <div className="article-layout-main-header-content flex w-full gap-4 p-4">
            <div className={`${styles.contentContainer} flex flex-col w-full h-full`}>
              {articleData.image && (
                <>
                  <BzImage
                    alt={articleData.assets?.[0]?.alt ?? 'Article image'}
                    className="absolute inset-0 w-full h-full z-0"
                    fetchPriority="high"
                    layout="fill"
                    loader={({ src, width }) => `${src}?quality=85&auto=webp&width=${width}`}
                    loaderProps={{
                      width: deviceType === 'mobile' ? 400 : 850,
                    }}
                    objectFit="cover"
                    preload={true}
                    src={articleData.image}
                  />
                  <div className="absolute inset-0 bg-[rgba(25,41,64,0.85)] opacity-75 z-[1]" />
                </>
              )}
              <div className="content-wrapper">
                <div className="flex flex-col w-full items-start lg:flex-row">
                  {hasBadges && (
                    <div className="badges">
                      {isProOnly ? (
                        <span>
                          <ProBadge />
                        </span>
                      ) : (
                        <span>{EdgeBadge}</span>
                      )}
                    </div>
                  )}
                  <ErrorBoundary name="article-layout-main-date-content">
                    <Suspense fallback={<div className="min-h-[26px] min-w-[320px]" />}>
                      <DateContent
                        created={createdAt ?? ''}
                        isDraft={isDraft}
                        readTime={calculateReadTime(articleData.body)}
                      />
                    </Suspense>
                  </ErrorBoundary>
                </div>
                {!!showAdvertiserDisclosure && <AdvertiserDisclosure slug={layout?.disclosure_slug} />}

                <h2 className="article-title text-2xl md:text-4xl text-white font-normal my-4">{title}</h2>

                {!hideTopPanel && (
                  <div className="flex flex-col w-full items-start lg:flex-row min-h-[25px]">
                    <ErrorBoundary name="article-layout-main-author-content">
                      <Suspense fallback={<div />}>
                        <NewAuthorContent
                          authorLink={articleData.author?.profileUrl}
                          authorName={authorName}
                          authorType={authorTypeGenerator(
                            articleData.author?.byLine ?? '',
                            showAdvertiserDisclosure,
                            contentType,
                          )}
                          authorUid={articleData.author?.uid}
                          className="flex flex-grow"
                          isTemplate={isTemplate}
                          twitter={articleData.author?.twitter}
                        />
                      </Suspense>
                    </ErrorBoundary>
                  </div>
                )}

                {articleData.title && (
                  <div
                    aria-label="Share"
                    className="share-buttons-row flex h-[40px] min-w-[250px] w-full max-w-[352px] mt-auto ml-auto gap-2 items-center"
                    role="group"
                  >
                    <Suspense fallback={<div aria-hidden className="h-[40px] w-[250px]" />}>
                      <FlipboardButton />
                      <GoogleNewsButton urlKey={googleNewsUrlKey} />
                      {!showAdvertiserDisclosure && (
                        <span aria-hidden className="inline-flex h-[40px] w-[144px] shrink-0">
                          {isApple ? <AppleNewsButton /> : null}
                        </span>
                      )}
                    </Suspense>
                  </div>
                )}
              </div>
            </div>

            {Array.isArray(featuredTickers) && featuredTickers.length > 0 ? (
              <div
                className={`article-layout-header-sidebar hidden md:flex flex-col gap-4 w-full max-w-[450px] min-h-0 ${sidebarLgMinH}`}
              >
                <Suspense fallback={<div className={`min-h-0 w-full ${sidebarLgMinH}`} />}>
                  <ArticleFeaturedTickersList tickers={featuredTickers} />
                </Suspense>
              </div>
            ) : (
              <div className="article-layout-header-sidebar hidden md:flex w-full max-w-[375px]">
                {!isTemplate && (
                  <React.Suspense fallback={<div className="min-h-[340px] min-w-[360px]" />}>
                    <CallToActionForm
                      // hubspotFormId="09985891-260e-4ba5-98b8-99d74c57e1ce"
                      beehiivFormId="c03f46e3-b180-439f-8cf4-103cbf2ac567"
                      styleOption="secondary"
                      subtitle="Enter your email to get Benzinga's ultimate morning update: The PreMarket Activity Newsletter"
                      title="Beat the Market With Our Free Pre-Market Newsletter"
                    />
                  </React.Suspense>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

NewArticleLayoutHeader.displayName = 'NewArticleLayoutHeader';
